import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, Mail, MessageCircle, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { usd } from "../../lib/affApi";

export const StatCard = ({ label, value, sub, testId, accent }) => (
  <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid={testId}>
    <div className="text-xs text-zinc-500">{label}</div>
    <div className={`font-display text-2xl lg:text-3xl mt-1 ${accent ? "text-emerald-700" : ""}`}>{value}</div>
    {sub && <div className="text-xs text-zinc-400 mt-0.5">{sub}</div>}
  </div>
);

export function OverviewCards({ cards, ratePct }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="aff-overview-cards">
      <StatCard label="Total earned" value={usd(cards.total_earned)} sub={`${ratePct}% commission`} testId="aff-card-earned" />
      <StatCard label="Pending payout" value={usd(cards.pending)} sub={`min. ${usd(cards.min_payout)}`} testId="aff-card-pending" accent />
      <StatCard label="Paid out" value={usd(cards.paid_out)} testId="aff-card-paid" />
      <StatCard label="Active customers" value={cards.active_customers} sub={`${usd(cards.this_month_estimate)} this month so far`} testId="aff-card-active" />
      <StatCard label="Link clicks" value={cards.clicks_30d} sub="last 30 days" testId="aff-card-clicks" />
      <StatCard label="Sign-ups" value={cards.signups_30d} sub="last 30 days" testId="aff-card-signups" />
    </div>
  );
}

const SHARE_TEXT = (link) =>
  `I use Seavanto to run yacht operations — inventory, maintenance, crew, documents in one place. Try it here: ${link}`;

export function LinkBlock({ code, link }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed — select the link and copy manually."); }
  };
  const txt = encodeURIComponent(SHARE_TEXT(link));
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 items-center" data-testid="aff-link-block">
      <div className="min-w-0">
        <div className="text-xs tracking-[0.2em] uppercase font-semibold text-zinc-500">Your referral link</div>
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 min-w-0 truncate rounded-md bg-zinc-50 border border-zinc-200 px-3 py-2 text-sm" data-testid="aff-link-url">{link}</code>
          <button onClick={copy} className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-ink text-white px-3 py-2 text-sm hover:bg-black" data-testid="aff-link-copy">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy
          </button>
        </div>
        <div className="mt-2 text-xs text-zinc-500">Code <b className="font-mono text-ink" data-testid="aff-link-code">{code}</b> — customers can also type it at registration.</div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <a className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-3 py-1.5 hover:border-zinc-400" href={`mailto:?subject=${encodeURIComponent("Have a look at Seavanto")}&body=${txt}`} data-testid="aff-share-email"><Mail className="h-3.5 w-3.5" /> Email</a>
          <a className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-3 py-1.5 hover:border-zinc-400" href={`https://wa.me/?text=${txt}`} target="_blank" rel="noreferrer" data-testid="aff-share-whatsapp"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
          <a className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-3 py-1.5 hover:border-zinc-400" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`} target="_blank" rel="noreferrer" data-testid="aff-share-linkedin"><Linkedin className="h-3.5 w-3.5" /> LinkedIn</a>
        </div>
      </div>
      <div className="justify-self-center rounded-lg border border-zinc-200 p-2 bg-white" data-testid="aff-link-qr">
        <QRCodeSVG value={link} size={112} fgColor="#09182A" />
      </div>
    </div>
  );
}
